import React, { useEffect, useState, useRef, useCallback } from 'react';
import { 
    Row, 
    Col, 
    Card, 
    CardBody, 
    CardHeader, 
    Button, 
    Badge, 
    Input, 
    Table, 
    Modal, 
    ModalHeader, 
    ModalBody, 
    ModalFooter,
    Progress,
    ButtonGroup
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faExclamationTriangle, 
    faSync, 
    faExpand, 
    faCompress, 
    faCheckCircle, 
    faClock, 
    faUser, 
    faMapMarkerAlt, 
    faTractor, 
    faImage, 
    faThLarge, 
    faList, 
    faCheck, 
    faTimes,
    faCalendarDay,
    faExclamationCircle,
    faSearch
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-multi-lang';
import { useToasts } from 'react-toast-notifications';
import FaultsService from '../Services/FaultsService';
import { MachinesService } from '../Services/MachinesService';
import QuarriesService from '../Services/QuarriesService';
import AuthService from '../Services/AuthService';
import { useFullscreen } from '../Contexts/FullscreenContext';
import { DateTime } from 'luxon';

const REFRESH_INTERVAL_SECONDS = 60;

const getUserDisplayName = (user, userId) => {
    if (!user) return userId ? String(userId) : '-';
    if (typeof user === 'string') return user;
    if (typeof user === 'object') {
        return user.name || user.username || (userId ? String(userId) : '-');
    }
    return String(user);
};

const isFaultResolved = (f) => Boolean(f?.isResolved ?? f?.resolved);
const isFaultSynced = (f) => Boolean(f?.isSyncedWithERP ?? f?.synced);
const getFaultArea = (f) => f?.area || f?.shelf || '';
const getFaultComments = (f) => f?.comments || f?.message || '';

const LiveFaults = () => {
    const t = useTranslation();
    const { addToast } = useToasts();
    const { isFullscreen, toggleFullscreen, setIsFullscreen } = useFullscreen();

    const [faults, setFaults] = useState([]);
    const [machines, setMachines] = useState([]);
    const [quarries, setQuarries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(new Date());
    const [secondsLeft, setSecondsLeft] = useState(REFRESH_INTERVAL_SECONDS);
    const [currentTime, setCurrentTime] = useState(DateTime.now());

    // Display & Filter options
    const [statusFilter, setStatusFilter] = useState('unresolved'); // 'unresolved' | 'resolved' | 'all'
    const [selectedQuarry, setSelectedQuarry] = useState('');
    const [selectedArea, setSelectedArea] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'

    // Photo Modal
    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);

    const faultsService = useRef(new FaultsService()).current;
    const machinesService = useRef(new MachinesService()).current;
    const quarriesService = useRef(new QuarriesService()).current;
    const authService = useRef(new AuthService()).current;
    const currentConfig = authService.getConfig();

    // Clean up fullscreen on unmount
    useEffect(() => {
        return () => {
            setIsFullscreen(false);
        };
    }, [setIsFullscreen]);

    // Live clock for relative time calculations
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(DateTime.now());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Initial data fetch
    useEffect(() => {
        loadMetadata();
        fetchFaultsData();
    }, []);

    const loadMetadata = async () => {
        try {
            const [mRes, qRes] = await Promise.all([
                machinesService.getMachines(),
                quarriesService.getQuarries()
            ]);
            setMachines(mRes || []);
            setQuarries(qRes.data || []);
            if (currentConfig && currentConfig.whCode) {
                setSelectedQuarry(currentConfig.whCode);
            }
        } catch (e) {
            console.error('Error loading metadata:', e);
        }
    };

    const fetchFaultsData = useCallback(async (isManual = false) => {
        if (isManual) setLoading(true);
        try {
            const data = await faultsService.getFaults();
            setFaults(data || []);
            setLastRefreshed(new Date());
            setSecondsLeft(REFRESH_INTERVAL_SECONDS);
        } catch (e) {
            console.error('Error fetching faults:', e);
            if (isManual) {
                addToast(t('faults.loadError') || 'Σφάλμα κατά την ανάκτηση βλαβών', { appearance: 'error', autoDismiss: true });
            }
        } finally {
            if (isManual) setLoading(false);
        }
    }, [faultsService, addToast, t]);

    // 1-minute countdown and auto-refresh timer
    useEffect(() => {
        const interval = setInterval(() => {
            setSecondsLeft(prev => {
                if (prev <= 1) {
                    fetchFaultsData(false);
                    return REFRESH_INTERVAL_SECONDS;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [fetchFaultsData]);

    const handleResolve = async (id) => {
        try {
            await faultsService.resolve(id);
            addToast(t('faults.resolveSuccess') || 'Η βλάβη επιλύθηκε επιτυχώς', { appearance: 'success', autoDismiss: true });
            fetchFaultsData(false);
        } catch (e) {
            console.error('Error resolving fault:', e);
            addToast(t('faults.resolveError') || 'Σφάλμα κατά την επίλυση της βλάβης', { appearance: 'error', autoDismiss: true });
        }
    };

    const handleToggleSync = async (id) => {
        try {
            await faultsService.toggleSync(id);
            addToast(t('faults.syncToggled') || 'Η κατάσταση συγχρονισμού ενημερώθηκε', { appearance: 'success', autoDismiss: true });
            fetchFaultsData(false);
        } catch (e) {
            console.error('Error toggling sync:', e);
            addToast(t('faults.syncError') || 'Σφάλμα κατά την ενημέρωση συγχρονισμού', { appearance: 'error', autoDismiss: true });
        }
    };

    // Filter faults
    const filteredFaults = faults.filter(f => {
        // Status filter
        if (statusFilter === 'unresolved' && isFaultResolved(f)) return false;
        if (statusFilter === 'resolved' && !isFaultResolved(f)) return false;

        // Quarry / WH filter
        if (selectedQuarry && f.wh !== selectedQuarry) return false;

        // Area / Shelf filter
        if (selectedArea && getFaultArea(f) !== selectedArea) return false;

        // Search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const machine = machines.find(m => m.code === f.machineId);
            const mCode = (f.machineId || '').toLowerCase();
            const mName = (machine?.name || f.machine?.title || '').toLowerCase();
            const reason = (f.reason || '').toLowerCase();
            const user = (typeof f.user === 'object' ? (f.user?.name || f.user?.username || '') : String(f.user || '')).toLowerCase();
            const area = getFaultArea(f).toLowerCase();
            const comments = getFaultComments(f).toLowerCase();

            if (!mCode.includes(term) && !mName.includes(term) && !reason.includes(term) && !user.includes(term) && !area.includes(term) && !comments.includes(term)) {
                return false;
            }
        }
        return true;
    });

    // Extract all unique areas
    const availableAreas = Array.from(new Set(faults.map(f => getFaultArea(f)).filter(Boolean)));

    // Summary Statistics
    const totalUnresolved = faults.filter(f => !isFaultResolved(f)).length;
    const totalResolved = faults.filter(f => isFaultResolved(f)).length;
    const today = DateTime.now().startOf('day');
    const reportedToday = faults.filter(f => {
        if (!f.createdAt) return false;
        const faultDate = DateTime.fromISO(f.createdAt);
        return faultDate >= today;
    }).length;
    const pendingSyncCount = faults.filter(f => !isFaultSynced(f)).length;

    const getRelativeTime = (isoString) => {
        if (!isoString) return '-';
        const date = DateTime.fromISO(isoString);
        const diff = currentTime.diff(date, ['days', 'hours', 'minutes']).toObject();
        const days = Math.floor(diff.days || 0);
        const hours = Math.floor(diff.hours || 0);
        const mins = Math.floor(diff.minutes || 0);

        if (days > 0) return `${days}d ${hours}h ${t('liveFaults.ago') || 'πριν'}`;
        if (hours > 0) return `${hours}h ${mins}m ${t('liveFaults.ago') || 'πριν'}`;
        if (mins > 0) return `${mins}m ${t('liveFaults.ago') || 'πριν'}`;
        return t('liveFaults.justNow') || 'Μόλις τώρα';
    };

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        return DateTime.fromISO(isoString).setZone('Europe/Athens').toFormat('dd/MM/yyyy HH:mm');
    };

    return (
        <div className={`live-faults-screen ${isFullscreen ? 'p-3 bg-dark text-white' : 'p-3'}`}>
            {/* Top Toolbar */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div className="d-flex align-items-center gap-2">
                    <h3 className="mb-0 fw-bold d-flex align-items-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
                        {t('liveFaults.title') || 'Ζωντανή Παρακολούθηση Βλαβών'}
                    </h3>
                    <Badge color="danger" pill className="fs-6 px-3 py-1">
                        {totalUnresolved} {t('liveFaults.active') || 'Ενεργές'}
                    </Badge>
                </div>

                {/* 1-Minute Live Auto-refresh Indicator & Controls */}
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <div className="d-flex align-items-center bg-light text-dark px-3 py-1 rounded border">
                        <FontAwesomeIcon icon={faClock} className="text-primary me-2" />
                        <span className="small me-2">
                            {t('liveFaults.nextUpdate') || 'Επόμενη ανανέωση'}: <strong>{secondsLeft}s</strong>
                        </span>
                        <div style={{ width: 60 }}>
                            <Progress 
                                value={(secondsLeft / REFRESH_INTERVAL_SECONDS) * 100} 
                                color={secondsLeft < 10 ? "danger" : "primary"} 
                                style={{ height: 6 }} 
                            />
                        </div>
                    </div>

                    <Button 
                        color="secondary" 
                        size="sm" 
                        onClick={() => fetchFaultsData(true)} 
                        disabled={loading}
                        title={t('refresh') || 'Ανανέωση τώρα'}
                    >
                        <FontAwesomeIcon icon={faSync} spin={loading} className="me-1" />
                        {DateTime.fromJSDate(lastRefreshed).toFormat('HH:mm:ss')}
                    </Button>

                    <ButtonGroup size="sm">
                        <Button 
                            color={viewMode === 'grid' ? 'primary' : 'outline-primary'} 
                            onClick={() => setViewMode('grid')}
                            title={t('liveFaults.cardsView') || 'Προβολή Καρτών'}
                        >
                            <FontAwesomeIcon icon={faThLarge} />
                        </Button>
                        <Button 
                            color={viewMode === 'table' ? 'primary' : 'outline-primary'} 
                            onClick={() => setViewMode('table')}
                            title={t('liveFaults.tableView') || 'Προβολή Πίνακα'}
                        >
                            <FontAwesomeIcon icon={faList} />
                        </Button>
                    </ButtonGroup>

                    <Button 
                        color={isFullscreen ? "danger" : "primary"} 
                        size={isFullscreen ? "md" : "sm"}
                        onClick={toggleFullscreen}
                        className={isFullscreen ? "fw-bold shadow" : "fw-bold"}
                        title={isFullscreen ? (t('fullscreen.exit') || "Κανονική Προβολή") : (t('fullscreen.enter') || "Πλήρης Οθόνη")}
                    >
                        <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} className="me-1" />
                        {isFullscreen ? (t('fullscreen.exit') || 'Κανονική Προβολή') : (t('fullscreen.enter') || 'Πλήρης Οθόνη')}
                    </Button>
                </div>
            </div>

            {/* Metrics Overview Cards */}
            <Row className="g-3 mb-3">
                <Col xs={6} md={3}>
                    <Card className={`border-0 shadow-sm ${isFullscreen ? 'bg-secondary text-white' : 'bg-white'}`}>
                        <CardBody className="p-3 d-flex align-items-center justify-content-between">
                            <div>
                                <div className="text-muted small text-uppercase fw-bold">{t('liveFaults.unresolved') || 'Ενεργές Βλάβες'}</div>
                                <h2 className="mb-0 text-danger fw-bold">{totalUnresolved}</h2>
                            </div>
                            <div className="bg-danger bg-opacity-10 p-3 rounded-circle">
                                <FontAwesomeIcon icon={faExclamationCircle} size="2x" className="text-danger" />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card className={`border-0 shadow-sm ${isFullscreen ? 'bg-secondary text-white' : 'bg-white'}`}>
                        <CardBody className="p-3 d-flex align-items-center justify-content-between">
                            <div>
                                <div className="text-muted small text-uppercase fw-bold">{t('liveFaults.today') || 'Σημερινές Βλάβες'}</div>
                                <h2 className="mb-0 text-warning fw-bold">{reportedToday}</h2>
                            </div>
                            <div className="bg-warning bg-opacity-10 p-3 rounded-circle">
                                <FontAwesomeIcon icon={faCalendarDay} size="2x" className="text-warning" />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card className={`border-0 shadow-sm ${isFullscreen ? 'bg-secondary text-white' : 'bg-white'}`}>
                        <CardBody className="p-3 d-flex align-items-center justify-content-between">
                            <div>
                                <div className="text-muted small text-uppercase fw-bold">{t('liveFaults.resolved') || 'Επιλυμένες'}</div>
                                <h2 className="mb-0 text-success fw-bold">{totalResolved}</h2>
                            </div>
                            <div className="bg-success bg-opacity-10 p-3 rounded-circle">
                                <FontAwesomeIcon icon={faCheckCircle} size="2x" className="text-success" />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card className={`border-0 shadow-sm ${isFullscreen ? 'bg-secondary text-white' : 'bg-white'}`}>
                        <CardBody className="p-3 d-flex align-items-center justify-content-between">
                            <div>
                                <div className="text-muted small text-uppercase fw-bold">{t('liveFaults.pendingSync') || 'Εκκρεμεί ERP'}</div>
                                <h2 className="mb-0 text-info fw-bold">{pendingSyncCount}</h2>
                            </div>
                            <div className="bg-info bg-opacity-10 p-3 rounded-circle">
                                <FontAwesomeIcon icon={faSync} size="2x" className="text-info" />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Filter Controls Card */}
            <Card className={`mb-3 shadow-sm ${isFullscreen ? 'bg-secondary text-white border-0' : ''}`}>
                <CardBody className="py-2">
                    <Row className="g-2 align-items-center">
                        <Col xs={12} sm={6} md={3} lg={2}>
                            <Input 
                                type="select" 
                                value={statusFilter} 
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="form-select-sm"
                            >
                                <option value="unresolved">{t('liveFaults.filterUnresolved') || 'Μόνο Ενεργές'}</option>
                                <option value="resolved">{t('liveFaults.filterResolved') || 'Μόνο Επιλυμένες'}</option>
                                <option value="all">{t('liveFaults.filterAll') || 'Όλες οι Βλάβες'}</option>
                            </Input>
                        </Col>
                        <Col xs={12} sm={6} md={3} lg={2}>
                            <Input 
                                type="select" 
                                value={selectedQuarry} 
                                onChange={(e) => setSelectedQuarry(e.target.value)}
                                className="form-select-sm"
                            >
                                <option value="">{t('liveFaults.allQuarries') || 'Όλα τα Λατομεία'}</option>
                                {quarries.map(q => (
                                    <option key={q.id} value={q.whCode}>{q.name}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col xs={12} sm={6} md={3} lg={2}>
                            <Input 
                                type="select" 
                                value={selectedArea} 
                                onChange={(e) => setSelectedArea(e.target.value)}
                                className="form-select-sm"
                            >
                                <option value="">{t('liveFaults.allAreas') || 'Όλα τα Πατάρια'}</option>
                                {availableAreas.map(a => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col xs={12} sm={6} md={3} lg={6}>
                            <div className="position-relative">
                                <Input 
                                    type="text" 
                                    placeholder={t('liveFaults.searchPlaceholder') || 'Αναζήτηση μηχανήματος, αιτίας, χειριστή...'} 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="form-control-sm ps-4"
                                />
                                <FontAwesomeIcon 
                                    icon={faSearch} 
                                    className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" 
                                />
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Content: Cards View or Table View */}
            {filteredFaults.length === 0 ? (
                <Card className={`text-center p-5 shadow-sm ${isFullscreen ? 'bg-secondary text-white border-0' : ''}`}>
                    <CardBody>
                        <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
                        <h4>{t('liveFaults.noFaultsFound') || 'Δεν υπάρχουν καταγεγραμμένες βλάβες'}</h4>
                        <p className="text-muted mb-0">
                            {t('liveFaults.allOperational') || 'Όλα τα μηχανήματα λειτουργούν κανονικά σύμφωνα με τα επιλεγμένα φίλτρα.'}
                        </p>
                    </CardBody>
                </Card>
            ) : viewMode === 'grid' ? (
                <Row className="g-3">
                    {filteredFaults.map(fault => {
                        const machine = machines.find(m => m.code === fault.machineId);
                        const relTime = getRelativeTime(fault.createdAt);
                        const formattedTime = formatDate(fault.createdAt);

                        return (
                            <Col key={fault.id} xs={12} sm={6} lg={4} xl={3}>
                                <Card 
                                    className={`h-100 shadow-sm border-0 position-relative ${
                                        isFaultResolved(fault) 
                                            ? (isFullscreen ? 'bg-secondary text-white' : 'bg-white border-success') 
                                            : (isFullscreen ? 'bg-secondary text-white border-danger border-2' : 'bg-white border-danger border-2')
                                    }`}
                                >
                                    <div 
                                        className={`position-absolute top-0 start-0 bottom-0 rounded-start ${
                                            isFaultResolved(fault) ? 'bg-success' : 'bg-danger'
                                        }`} 
                                        style={{ width: 6 }} 
                                    />
                                    <CardBody className="p-3 ps-4 d-flex flex-column justify-content-between">
                                        <div>
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div>
                                                    <h5 className="mb-0 fw-bold d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faTractor} className="me-2 text-primary" />
                                                        {fault.machineId}
                                                    </h5>
                                                    {(machine?.name || fault.machine?.title) && (
                                                        <small className="text-muted d-block">{machine?.name || fault.machine?.title}</small>
                                                    )}
                                                </div>
                                                <div className="d-flex gap-1 flex-wrap justify-content-end">
                                                    <Badge color={fault.isOperable !== false ? "info" : "danger"} className="p-2">
                                                        {fault.isOperable !== false ? (t('faults.operableShort') || 'Λειτουργικό') : (t('faults.disabledShort') || 'Εκτός λειτουργίας')}
                                                    </Badge>
                                                    <Badge color={isFaultResolved(fault) ? "success" : "danger"} className="p-2">
                                                        {isFaultResolved(fault) 
                                                            ? (t('liveFaults.resolved') || 'Επιλυμένη') 
                                                            : (t('liveFaults.unresolved') || 'Σε Εκκρεμότητα')
                                                        }
                                                    </Badge>
                                                </div>
                                            </div>

                                            <div className="p-2 rounded bg-light text-dark mb-2">
                                                <div className="fw-bold text-danger mb-1">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                                    {fault.reason}
                                                </div>
                                                {getFaultComments(fault) && (
                                                    <small className="text-muted d-block">
                                                        {getFaultComments(fault)}
                                                    </small>
                                                )}
                                            </div>

                                            <div className="small mb-2">
                                                <div className="d-flex justify-content-between py-1 border-bottom border-light">
                                                    <span className="text-muted">
                                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                                        {t('liveFaults.area') || 'Πατάρι'}:
                                                    </span>
                                                    <span className="fw-bold">{getFaultArea(fault) || '-'} (WH: {fault.wh || '-'})</span>
                                                </div>
                                                <div className="d-flex justify-content-between py-1 border-bottom border-light">
                                                    <span className="text-muted">
                                                        <FontAwesomeIcon icon={faUser} className="me-1" />
                                                        {t('liveFaults.reportedBy') || 'Δηλώθηκε από'}:
                                                    </span>
                                                    <span>{getUserDisplayName(fault.user, fault.userId)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between py-1">
                                                    <span className="text-muted">
                                                        <FontAwesomeIcon icon={faClock} className="me-1" />
                                                        {t('liveFaults.time') || 'Ώρα'}:
                                                    </span>
                                                    <span title={formattedTime}><strong>{relTime}</strong> ({formattedTime})</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="pt-2 border-top border-light d-flex justify-content-between align-items-center gap-1">
                                            <div>
                                                {fault.photoUrl && (
                                                    <Button 
                                                        size="sm" 
                                                        color="info" 
                                                        outline 
                                                        onClick={() => {
                                                            setSelectedPhoto(fault.photoUrl);
                                                            setPhotoModalOpen(true);
                                                        }}
                                                        title={t('liveFaults.viewPhoto') || 'Προβολή Φωτογραφίας'}
                                                    >
                                                        <FontAwesomeIcon icon={faImage} />
                                                    </Button>
                                                )}
                                                {!isFaultSynced(fault) && (
                                                    <Button 
                                                        size="sm" 
                                                        color="warning" 
                                                        outline 
                                                        className="ms-1"
                                                        onClick={() => handleToggleSync(fault.id)}
                                                        title={t('faults.toggleSync') || "Sync to ERP"}
                                                    >
                                                        <FontAwesomeIcon icon={faSync} className="me-1" />
                                                        {t('faults.toggleSync') || 'Sync'}
                                                    </Button>
                                                )}
                                            </div>

                                            {!isFaultResolved(fault) && (
                                                <Button 
                                                    size="sm" 
                                                    color="success" 
                                                    onClick={() => handleResolve(fault.id)}
                                                >
                                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                    {t('liveFaults.resolveBtn') || 'Επίλυση'}
                                                </Button>
                                            )}
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            ) : (
                <Card className={`shadow-sm border-0 ${isFullscreen ? 'bg-secondary text-white' : ''}`}>
                    <CardBody className="p-0 overflow-auto">
                        <Table responsive hover striped className={`mb-0 align-middle ${isFullscreen ? 'table-dark' : ''}`}>
                            <thead className="table-light text-dark sticky-top">
                                <tr>
                                    <th>{t('liveFaults.status') || 'Κατάσταση'}</th>
                                    <th>{t('liveFaults.machine') || 'Μηχάνημα'}</th>
                                    <th>{t('liveFaults.reason') || 'Αιτία Βλάβης'}</th>
                                    <th>{t('liveFaults.area') || 'Πατάρι / WH'}</th>
                                    <th>{t('liveFaults.reportedBy') || 'Χειριστής'}</th>
                                    <th>{t('liveFaults.time') || 'Χρόνος'}</th>
                                    <th>{t('liveFaults.erpSync') || 'ERP'}</th>
                                    <th>{t('liveFaults.actions') || 'Ενέργειες'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredFaults.map(fault => {
                                    const machine = machines.find(m => m.code === fault.machineId);
                                    const relTime = getRelativeTime(fault.createdAt);
                                    const formattedTime = formatDate(fault.createdAt);

                                    return (
                                        <tr key={fault.id}>
                                            <td>
                                                <div className="d-flex flex-column gap-1">
                                                    <Badge color={isFaultResolved(fault) ? "success" : "danger"} className="p-2">
                                                        {isFaultResolved(fault) 
                                                            ? <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> 
                                                            : <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                                        }
                                                        {isFaultResolved(fault) 
                                                            ? (t('liveFaults.resolved') || 'Επιλυμένη') 
                                                            : (t('liveFaults.unresolved') || 'Ενεργή')
                                                        }
                                                    </Badge>
                                                    <Badge color={fault.isOperable !== false ? "info" : "danger"}>
                                                        {fault.isOperable !== false ? (t('faults.operableShort') || 'Λειτουργικό') : (t('faults.disabledShort') || 'Εκτός λειτουργίας')}
                                                    </Badge>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="fw-bold">{fault.machineId}</div>
                                                {(machine?.name || fault.machine?.title) && <small className="text-muted">{machine?.name || fault.machine?.title}</small>}
                                            </td>
                                            <td>
                                                <div className="fw-bold text-danger">{fault.reason}</div>
                                                {getFaultComments(fault) && <small className="text-muted">{getFaultComments(fault)}</small>}
                                            </td>
                                            <td>
                                                <Badge color="info" className="text-dark">
                                                    {getFaultArea(fault) || '-'}
                                                </Badge>
                                                <small className="d-block text-muted">WH: {fault.wh || '-'}</small>
                                            </td>
                                            <td>{getUserDisplayName(fault.user, fault.userId)}</td>
                                            <td>
                                                <div><strong>{relTime}</strong></div>
                                                <small className="text-muted">{formattedTime}</small>
                                            </td>
                                            <td>
                                                {!isFaultSynced(fault) ? (
                                                    <Button 
                                                        size="sm" 
                                                        color="warning" 
                                                        outline 
                                                        onClick={() => handleToggleSync(fault.id)}
                                                    >
                                                        <FontAwesomeIcon icon={faSync} className="me-1" />
                                                        {t('faults.toggleSync') || 'Sync'}
                                                    </Button>
                                                ) : (
                                                    <Badge color="success">{t('faults.yes') || 'Synced'}</Badge>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    {fault.photoUrl && (
                                                        <Button 
                                                            size="sm" 
                                                            color="info" 
                                                            outline 
                                                            onClick={() => {
                                                                setSelectedPhoto(fault.photoUrl);
                                                                setPhotoModalOpen(true);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faImage} />
                                                        </Button>
                                                    )}
                                                    {!isFaultResolved(fault) && (
                                                        <Button 
                                                            size="sm" 
                                                            color="success" 
                                                            onClick={() => handleResolve(fault.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                            {t('liveFaults.resolveBtn') || 'Επίλυση'}
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>
            )}

            {/* Photo Modal Preview */}
            <Modal isOpen={photoModalOpen} toggle={() => setPhotoModalOpen(!photoModalOpen)} size="lg" centered>
                <ModalHeader toggle={() => setPhotoModalOpen(!photoModalOpen)}>
                    {t('liveFaults.damagePhoto') || 'Φωτογραφία Βλάβης'}
                </ModalHeader>
                <ModalBody className="text-center p-2 bg-dark">
                    {selectedPhoto ? (
                        <img 
                            src={selectedPhoto} 
                            alt="Damage report" 
                            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} 
                        />
                    ) : (
                        <p className="text-muted">{t('liveFaults.noPhotoAvailable') || 'Δεν υπάρχει διαθέσιμη φωτογραφία'}</p>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setPhotoModalOpen(false)}>
                        {t('close') || 'Κλείσιμο'}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default LiveFaults;
